// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
import { Controller } from "@hotwired/stimulus"

/**
 * Pagination Jumper Controller
 * @gem rapid_rails_ui
 * @version 0.85.7
 * @updated 2026-01-17
 *
 * Handles go-to-page functionality for the Pagination component.
 * Supports both standard navigation and Turbo Frame integration.
 *
 * With perPage: true the element is the per-page GET form: a change submits
 * it and the Apply button hides. The listener is added in connect() rather
 * than by data-action, so markup served to an app on an older copy of this
 * controller raises no missing-action error and keeps a working Apply button.
 * A change made from the keyboard (arrows on a closed select fire change on
 * Windows and Linux) does not navigate: Apply reappears and Enter submits, so
 * a keyboard user can reach 50 without landing on 25 first.
 *
 * Usage:
 *   <div data-controller="rapid-rails-ui--pagination-jumper"
 *        data-rapid-rails-ui--pagination-jumper-base-url-value="/posts"
 *        data-rapid-rails-ui--pagination-jumper-page-param-value="page"
 *        data-rapid-rails-ui--pagination-jumper-total-pages-value="20">
 *     <input type="number" data-rapid-rails-ui--pagination-jumper-target="input" />
 *   </div>
 */
export default class extends Controller {
  static targets = ["input", "apply"]
  static values = {
    baseUrl: String,
    pageParam: { type: String, default: "page" },
    totalPages: Number,
    perPage: { type: Boolean, default: false }
  }

  connect() {
    if (!this.perPageValue) return
    this._keyed = false
    this._listeners = {
      pointerdown: () => { this._keyed = false },
      keydown: (event) => this._perPageKeydown(event),
      change: () => this._perPageChange()
    }
    for (const [type, fn] of Object.entries(this._listeners)) this.element.addEventListener(type, fn)
    if (this.hasApplyTarget) this.applyTarget.hidden = true
  }

  disconnect() {
    if (!this._listeners) return
    for (const [type, fn] of Object.entries(this._listeners)) this.element.removeEventListener(type, fn)
  }

  _perPageKeydown(event) {
    if (event.target.tagName !== "SELECT" || event.key === "Tab") return
    if (event.key === "Enter" && this._keyed) {
      event.preventDefault()
      this.element.requestSubmit()
      return
    }
    this._keyed = true
  }

  _perPageChange() {
    if (!this._keyed) {
      this.element.requestSubmit()
    } else if (this.hasApplyTarget) {
      this.applyTarget.hidden = false
    }
  }

  /**
   * Navigate to the page entered in the input
   */
  navigate(event) {
    // Prevent default form submission if applicable
    if (event.type === "keydown") {
      event.preventDefault()
    }

    const page = parseInt(this.inputTarget.value, 10)

    // Validate page number
    if (isNaN(page) || page < 1 || page > this.totalPagesValue) {
      // Reset to valid range
      this.inputTarget.value = Math.max(1, Math.min(page || 1, this.totalPagesValue))
      return
    }

    // Build the URL
    const url = this._buildUrl(page)

    // Check for Turbo Frame
    const turboFrame = this.element.dataset.turboFrame
    if (turboFrame) {
      // Use Turbo to visit the URL within the frame
      const frame = document.getElementById(turboFrame)
      if (frame) {
        frame.src = url
      } else {
        // Fallback to Turbo visit
        Turbo.visit(url)
      }
    } else {
      // Standard navigation
      window.location.href = url
    }
  }

  /**
   * Build the URL with the page parameter
   * @param {number} page - The page number
   * @returns {string} - The constructed URL
   */
  _buildUrl(page) {
    try {
      const url = new URL(this.baseUrlValue, window.location.origin)
      url.searchParams.set(this.pageParamValue, page.toString())
      return url.toString()
    } catch (e) {
      // Fallback for relative URLs
      const separator = this.baseUrlValue.includes("?") ? "&" : "?"
      return `${this.baseUrlValue}${separator}${this.pageParamValue}=${page}`
    }
  }
}
